import { FormGroup, Input } from "reactstrap";
import pluralize from "pluralize";
import { useMemo } from "react";

import { SharedSubviewRecordsInfo } from "../../../types/GlobalInterfaces";
import ModuleFieldsPicker from "../../TableLayouts/ModuleFieldsPicker";
import CollapseItem from "../../FormBuilder/components/CollapseItem";
import useSubViews from "../../../api/api-hooks/views/useSubViews";
import { Field, Module } from "../../../types/TenantInterfaces";
import RequiredTitle from "../../../elements/RequiredTitle";
import ReadableFilter from "../../CRM/ReadableFilter";
import { View } from "../../../types/PageInterfaces";
import CustomToggle from "../Fields/CustomToggle";
import useModule from "../../../hooks/useModule";

interface Props {
  pageID: string | undefined;
  viewID: string | undefined;
  enableSubviewRecords: boolean;
  setEnableSubviewRecords: React.Dispatch<React.SetStateAction<boolean>>;
  subviewRecordInfo: SharedSubviewRecordsInfo;
  setSubviewRecordInfo: React.Dispatch<
    React.SetStateAction<SharedSubviewRecordsInfo>
  >;
  subviewRecordLimit: number;
  setSubviewRecordLimit: React.Dispatch<React.SetStateAction<number>>;
}

export default function SharedRecordSubviewsTab({
  pageID,
  viewID,
  enableSubviewRecords,
  setEnableSubviewRecords,
  subviewRecordInfo,
  setSubviewRecordInfo,
  subviewRecordLimit,
  setSubviewRecordLimit,
}: Props) {
  const { getModule } = useModule();

  const { subviews, subviewsLoading } = useSubViews({
    pageID,
    parentViewID: viewID,
  });

  const subViewModules = useMemo<
    Record<
      string,
      {
        ModName: string;
        Views: View[];
        Fields: Field[];
        Module: Module;
      }
    >
  >(() => {
    if (subviewsLoading || !subviews.length) return {};

    let data: Record<
      string,
      {
        ModName: string;
        Views: View[];
        Fields: Field[];
        Module: Module;
      }
    > = {};

    for (const subview of subviews) {
      const mod = subview?.ModuleAPIName;
      const foundModule = getModule(mod, false, true);

      if (!foundModule) continue;

      if (!data[mod]) {
        data[mod] = {
          ModName: foundModule.Name,
          Module: foundModule,
          Fields: foundModule?.Fields,
          Views: [],
        };
      }

      data[mod].Views.push(subview);
    }

    return data;
  }, [subviews, subviewsLoading]);

  return (
    <div className="pt-4">
      <CustomToggle
        title="Share Related Records"
        description="When enabled, you can add records from your SubViews to be shared with this record."
        value={enableSubviewRecords}
        handleChange={(e: any) => setEnableSubviewRecords(e.target.checked)}
        containerCSS="mb-2"
      />
      {enableSubviewRecords && (
        <>
          <RequiredTitle title="Record Limit" size="md" titleCSS="mb-0" />
          <div className="small mb-2">
            The maximum number of records that can be shared
          </div>
          <Input
            type="number"
            className="mb-4"
            value={subviewRecordLimit}
            onChange={(e) => {
              let newValue = parseInt(e.target.value);
              setSubviewRecordLimit(
                newValue > 500 ? 500 : newValue < 1 ? 1 : newValue
              );
            }}
          />
          <h4>SubView Data Sources</h4>
          {Object.keys(subViewModules).map((mod) => {
            const modName = subViewModules[mod].ModName;
            const modViews = subViewModules[mod].Views;
            const availableFields = subViewModules[mod].Fields;
            const moduleSchema = subViewModules?.[mod]?.Module;

            const hasViewFilter = modViews.some(
              (v) => !!v?.ViewFilters?.Filters?.length
            );

            if (!hasViewFilter) return null;

            return (
              <CollapseItem key={modName} title={modName}>
                {modViews.map((subView) => {
                  if (!subView?.ViewFilters?.Filters?.length) return null;

                  const enabled = !!subviewRecordInfo?.[mod]?.Views?.find(
                    (v) => v.ViewID === subView.id
                  );
                  const selectedFields = subviewRecordInfo?.[mod]?.Fields ?? [];

                  return (
                    <div>
                      <CustomToggle
                        containerCSS="mb-2"
                        title={`Share ${pluralize(modName)}? `}
                        description={`Enable this to share ${pluralize(
                          modName
                        )} matching the following View Filters`}
                        value={enabled}
                        handleChange={() => {
                          setSubviewRecordInfo((prev) => {
                            const newInfo = { ...prev };

                            if (!newInfo[mod]) {
                              newInfo[mod] = { Views: [], Fields: [] };
                            }

                            const alreadyAdded = !!newInfo?.[mod]?.Views?.find(
                              (v) => v.ViewID === subView.id
                            );

                            // Adding new SubViews.
                            if (!alreadyAdded) {
                              newInfo[mod].Views.push({
                                ViewID: subView.id,
                                ViewFilters: subView.ViewFilters,
                              });

                              return newInfo;
                            }

                            // Removing SubViews.
                            newInfo[mod].Views = newInfo[mod].Views.filter(
                              (v) => v.ViewID !== subView.id
                            );

                            if (!newInfo[mod].Views.length) delete newInfo[mod];

                            return newInfo;
                          });
                        }}
                      />
                      <h5>{subView?.Name} SubView Filter</h5>
                      <ReadableFilter
                        query={subView.ViewFilters}
                        moduleAPIName={mod}
                        onClick={undefined}
                        disabled={true}
                      />

                      {enabled && (
                        <>
                          <FormGroup className="mt-3">
                            <h5 className="mb-0">Shared Fields</h5>
                            <div className="mb--2">
                              <small>
                                Select fields to be shared from this module.
                              </small>
                            </div>
                          </FormGroup>
                          <ModuleFieldsPicker
                            values={selectedFields}
                            availableFields={availableFields}
                            module={moduleSchema}
                            setError={() => {}}
                            setValues={(fields) => {
                              setSubviewRecordInfo((prev) => {
                                const newInfo = { ...prev };
                                newInfo[mod].Fields = fields;

                                return newInfo;
                              });
                            }}
                            onChange={(fields) => {
                              setSubviewRecordInfo((prev) => {
                                const newInfo = { ...prev };
                                newInfo[mod].Fields = fields;

                                return newInfo;
                              });
                            }}
                            placeholder="Select fields to share."
                          />
                        </>
                      )}
                    </div>
                  );
                })}
              </CollapseItem>
            );
          })}
        </>
      )}
    </div>
  );
}
